import { ChatComposeBoxAddAttachmentMenuItem } from '@glade-ai/chat-plugin/ui';
import React, { type ComponentPropsWithoutRef, type FC, forwardRef, useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import SearchInput from '@/components/FormFields/SearchInput';
import Signature from '@/components/Icons/Signature';
import ProgressIndicator from '@/components/ProgressIndicator';
import useNoodleApi from '@/hooks/useNoodleApi';
import TeamsContext from '@/providers/Teams/TeamsContext';
import * as tsClient from '@/tsClient';
import type * as ApiModels from '@/typings/api-models';

import s from '../InputComposer.module.scss';

type Props = {
  onAttach: (data: { customTermsTemplateId: string; title: string }) => void;
};

const ADD_CUSTOM_TERMS_LABEL = 'Custom Terms';
const ADD_CUSTOM_TERMS_ICON = 'ph--signature--regular';

export const AddCustomTermsMenuItem = forwardRef<
  HTMLDivElement,
  Omit<ComponentPropsWithoutRef<typeof ChatComposeBoxAddAttachmentMenuItem>, 'icon' | 'label'>
>((props, forwardedRef) => (
  <ChatComposeBoxAddAttachmentMenuItem
    {...props}
    ref={forwardedRef}
    icon={ADD_CUSTOM_TERMS_ICON}
    label={ADD_CUSTOM_TERMS_LABEL}
  />
));
AddCustomTermsMenuItem.displayName = 'AddCustomTermsMenuItem';

export const AddCustomTermsPopoverContent: FC<Props> = ({ onAttach }) => {
  const { creatorId } = useContext(TeamsContext);
  const [search, setSearch] = useState('');
  const {
    data: customTerms,
    getData: getCustomTerms,
    fetchingState: { isFetching: isFetchingCustomTerms },
  } = useNoodleApi(tsClient.customTerms.getCustomTermsTemplates);

  useEffect(() => {
    if (creatorId) {
      getCustomTerms({ creatorId, page: 1, perPage: 1000 });
    }
  }, [creatorId, getCustomTerms]);

  const handleClick = (template: Pick<ApiModels.CustomTermsTemplate, 'id' | 'title'>): void => {
    onAttach({ customTermsTemplateId: template.id, title: template.title || template.id });
  };

  if (customTerms?.items.length === 0) {
    return null;
  }

  // TODO(will): This is clearly a combobox and should be refactored to be such.
  return (
    <>
      {!customTerms || isFetchingCustomTerms ? (
        <ProgressIndicator isCentered />
      ) : (
        <>
          <SearchInput
            label='Filter by title'
            placeholder='Type a title...'
            setSearch={setSearch}
            className={s.search}
          />
          {customTerms.items
            .filter((template) => template.title?.toLowerCase().includes(search.toLowerCase()))
            .map((template) => (
              <Buttons
                className={s.documentRequestButton}
                key={template.id}
                isWrapper
                isFlat
                onClick={() => handleClick(template)}
              >
                <Signature size={14} weight='fill' color='var(--color-primary)' />
                <p>{template.title}</p>
              </Buttons>
            ))}
        </>
      )}
    </>
  );
};
